import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormSection } from '../FormSection';
import { FormElement } from '../FormElement';
import { InputText } from '../Inputs/InputText';
import { InputPostalCode } from '../Inputs/InputPostalCode';
import { InputPhone } from '../Inputs/InputPhone';
import { StatusInfo } from './StatusInfo';
import { FormSubmitActions } from '../FormSubmitActions';
import { toast } from '@/hooks/useToast';
import {
  getListOrganizationApplicationsQueryKey,
  OrganizationApplicationDTO,
} from '@/api/trafikportalen';
import { updateOrganizationApplicationBody } from '@/api/trafikportalen/trafikportalenAPI.zod';
import { formatDate } from '@/helpers/formatting';
import { useUpdateOrganizationApplication } from '@/api/trafikportalen/trafikportalenAPI';
import { useQueryClient } from '@tanstack/react-query';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import {
  EINVOICE_EMAIL_MAX_LENGTH,
  normalizeGlnInput,
  optionalTrimmedText,
  validateInvoicingIdentifiers,
} from '@/helpers/invoicingIdentifiers';
import { trimData } from '@/helpers/trimData';
import { useTenantID } from '@/hooks/useTenantID';

type ApplicationInformationType = z.infer<
  typeof updateOrganizationApplicationBody
>;

interface ApplicationInformationFormProps {
  application?: OrganizationApplicationDTO;
}

export const ApplicationInformationForm = ({
  application,
}: ApplicationInformationFormProps) => {
  const tenantId = useTenantID();

  const [editMode, setEditMode] = useState(false);
  const [applicationValues, setApplicationValues] = useState<
    ApplicationInformationType | undefined
  >(application);

  const customErrorMap = useCustomErrorMap;

  const { formatMessage } = useIntl();

  const queryClient = useQueryClient();

  const { mutate: updateOrganizationApplication } =
    useUpdateOrganizationApplication();

  const schema = z
    .preprocess((data) => {
      const values = data as ApplicationInformationType;
      return {
        ...values,
        gln: optionalTrimmedText(values.gln),
        einvoice_email: optionalTrimmedText(values.einvoice_email),
      };
    }, updateOrganizationApplicationBody)
    .superRefine((data, ctx) => {
      validateInvoicingIdentifiers({
        values: data,
        ctx,
        formatMessage,
      });
    });

  const methods = useForm<ApplicationInformationType>({
    defaultValues: {
      ...applicationValues,
    },
    resolver: zodResolver(schema, {
      errorMap: customErrorMap(),
    }),
    mode: 'onBlur',
  });

  const startEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditMode(false);
    methods.reset();
  }, [methods]);

  useEffect(() => {
    if (application) {
      setApplicationValues(application);
      methods.reset(application);
    }
  }, [application, methods]);

  const organizationNumber = methods.watch('organization_number');

  useEffect(() => {
    if (!editMode || !organizationNumber) return;

    if (organizationNumber.includes(' ')) {
      methods.setValue(
        'organization_number',
        organizationNumber.replace(' ', ''),
      );
      return;
    }

    if (organizationNumber.includes('-')) return;

    if (organizationNumber.length > 6) {
      methods.setValue(
        'organization_number',
        `${organizationNumber.slice(0, 6)}-${organizationNumber.slice(6)}`,
      );
    }
  }, [editMode, organizationNumber, methods]);

  const phone = methods.watch('contact_phone');
  const phoneError = useMemo(() => {
    if (!phone) return '';
    if (phone.startsWith('+46')) return '';
    return formatMessage({
      defaultMessage: 'Telefonnummer måste börja med +46',
    });
  }, [formatMessage, phone]);

  if (!application) return;

  const onSubmit = (data: ApplicationInformationType) => {
    updateOrganizationApplication(
      {
        organizationApplicationID: application.id,
        data: trimData(data),
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getListOrganizationApplicationsQueryKey(),
          });
          toast.success(
            {
              title: formatMessage(
                {
                  defaultMessage:
                    'Ansökan med organisationsnummer {organizationNumber} uppdaterades',
                },
                {
                  organizationNumber: data.organization_number,
                },
              ),
            },
            { autoClose: 3000 },
          );
          cancelEdit();
        },
        onError: (error) => {
          console.error('Error: ', error);
          toast.error(
            {
              title: 'Ett fel inträffade',
              description:
                'Försök spara igen senare, eller vänligen kontakta support.',
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form
        className="flex w-full flex-col gap-4 sm:w-panel-full"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <FormSection
          buttonType={
            application.status === 'rejected' ? 'hideButton' : 'editButton'
          }
          title={<FormattedMessage defaultMessage="Ansöknings­information" />}
          onClickButton={startEdit}
          editMode={editMode}
          canSeeButton={checkUserPermissions({ permission: 'tenant:editor' })}
        >
          <FormElement
            label={<FormattedMessage defaultMessage="Ansökningsdatum" />}
            input={formatDate(application.created_at)}
          />
          <FormElement
            id="name"
            label={<FormattedMessage defaultMessage="Namn" />}
            input={
              <InputText
                name="name"
                control={methods.control}
                editMode={editMode}
                error={methods.formState.errors.name}
                helperTextReserveSpace
              />
            }
          />
          <FormElement
            id="organization_number"
            label={<FormattedMessage defaultMessage="Organisationsnummer" />}
            input={
              <InputText
                name="organization_number"
                placeholder="123456-1234"
                control={methods.control}
                editMode={editMode}
                error={methods.formState.errors.organization_number}
                helperTextReserveSpace
              />
            }
          />
          <FormElement
            label={<FormattedMessage defaultMessage="Status" />}
            input={
              <>
                <StatusInfo
                  status={application.status ?? ''}
                  updatedAt={
                    application.status === 'rejected'
                      ? formatDate(application.updated_at)
                      : ''
                  }
                />
                {application.status === 'rejected' && (
                  <p className="text-text-800">
                    {application.reason ? (
                      `"${application.reason}"`
                    ) : (
                      <i>
                        <FormattedMessage defaultMessage="Ingen anledning angiven" />
                      </i>
                    )}
                  </p>
                )}
              </>
            }
          />
          <div className="flex flex-col gap-2">
            <h4 className="text-lg">
              <FormattedMessage defaultMessage="Faktureringsuppgifter" />
            </h4>
            <FormElement
              id="invoicing_address.street"
              label={<FormattedMessage defaultMessage="Adress" />}
              input={
                <InputText
                  name="invoicing_address.street"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.invoicing_address?.street}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="invoicing_address.city"
              label={<FormattedMessage defaultMessage="Postort" />}
              input={
                <InputText
                  name="invoicing_address.city"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.invoicing_address?.city}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="invoicing_address.zip_code"
              label={<FormattedMessage defaultMessage="Postnummer" />}
              input={
                <InputPostalCode
                  name="invoicing_address.zip_code"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.delivery_address?.zip_code}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="invoicing_reference"
              label={<FormattedMessage defaultMessage="Referens" />}
              input={
                <InputText
                  name="invoicing_reference"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.invoicing_reference}
                  helperTextReserveSpace
                />
              }
            />
            {tenantId.toLowerCase() === 'ltr' && (
              <FormElement
                id="suggested_cost_center"
                label={
                  <FormattedMessage defaultMessage="Föreslaget kostnadsställe" />
                }
                input={
                  <InputText
                    name="suggested_cost_center"
                    control={methods.control}
                    editMode={editMode}
                    error={methods.formState.errors.suggested_cost_center}
                    helperTextReserveSpace
                  />
                }
              />
            )}
            <FormElement
              id="gln"
              label={<FormattedMessage defaultMessage="GLN-nummer" />}
              input={
                <InputText
                  name="gln"
                  type="number"
                  normalize={normalizeGlnInput}
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.gln}
                  noValueDescription={
                    <FormattedMessage defaultMessage="Inget GLN-nummer angivet" />
                  }
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="einvoice_email"
              label={<FormattedMessage defaultMessage="E-fakturaadress" />}
              input={
                <InputText
                  name="einvoice_email"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.einvoice_email}
                  placeholder="faktura@exempel.se"
                  noValueDescription={
                    <FormattedMessage defaultMessage="Ingen e-fakturaadress angiven" />
                  }
                  helperTextReserveSpace
                  maxLength={EINVOICE_EMAIL_MAX_LENGTH}
                />
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <h4 className="text-lg">
              <FormattedMessage defaultMessage="Leveransadress" />
            </h4>
            <FormElement
              id="delivery_address.street"
              label={<FormattedMessage defaultMessage="Adress" />}
              input={
                <InputText
                  name="delivery_address.street"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.delivery_address?.street}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="delivery_address.zip_code"
              label={<FormattedMessage defaultMessage="Postnummer" />}
              input={
                <InputPostalCode
                  name="delivery_address.zip_code"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.delivery_address?.zip_code}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="delivery_address.city"
              label={<FormattedMessage defaultMessage="Postort" />}
              input={
                <InputText
                  name="delivery_address.city"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.delivery_address?.city}
                  helperTextReserveSpace
                />
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <h4 className="text-lg">
              <FormattedMessage defaultMessage="Kontakt" />
            </h4>
            <FormElement
              id="contact_first_name"
              label={<FormattedMessage defaultMessage="Förnamn" />}
              input={
                <InputText
                  name="contact_first_name"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.contact_first_name}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="contact_last_name"
              label={<FormattedMessage defaultMessage="Efternamn" />}
              input={
                <InputText
                  name="contact_last_name"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.contact_last_name}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="contact_email"
              label={<FormattedMessage defaultMessage="E-post" />}
              input={
                <InputText
                  name="contact_email"
                  control={methods.control}
                  editMode={editMode}
                  error={methods.formState.errors.contact_email}
                  helperTextReserveSpace
                />
              }
            />
            <FormElement
              id="contact_phone"
              label={<FormattedMessage defaultMessage="Telefon" />}
              input={
                <InputPhone
                  name="contact_phone"
                  control={methods.control}
                  editMode={editMode}
                  placeholder="+46"
                  error={
                    methods.formState.errors.contact_phone || {
                      message: phoneError,
                      type: 'custom',
                    }
                  }
                  helperTextReserveSpace
                />
              }
            />
          </div>
        </FormSection>
        {editMode && (
          <FormSubmitActions
            active={true}
            createMode={false}
            onCancel={cancelEdit}
          />
        )}
      </form>
    </FormProvider>
  );
};
